import { useTranslation } from "react-i18next";
import Restricted from "@/consumer/restricted";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import { useMemo, useState } from "react";
import { MRT_ColumnDef } from "material-react-table";
import { replaceDate } from "@/helper/dateHandling";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { Tooltip } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { api } from "@/helper/api";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";

export default function TasktrackingTable() {
  const { t } = useTranslation();
  usePageHeading(t("tasktracking"));
  const { showConfirmationModalAsync } = useConfirmationModal();

  const [refreshTable, setRefreshTable] = useState<boolean>(false);
  const { handleError, errorModal } = useErrorModal();

  const statusFilterOptions = useMemo(() => {
    return [
      { label: t("open"), value: "open" },
      { label: t("in_progress"), value: "in_progress" },
      { label: t("completed"), value: "completed" },
      { label: t("canceled"), value: "canceled" },
    ];
  }, [t]);

  const tasktrackingColumns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "id",
        header: t("id"),
        id: "id",
        enableHiding: false,
        enableGlobalFilter: false,
      },
      {
        accessorKey: "task_id",
        header: t("task_id"),
        id: "task_id",
        enableGlobalFilter: false,
      },
      {
        accessorKey: "status",
        header: t("status"),
        id: "status",
        enableGlobalFilter: false,
        filterVariant: "select",
        filterSelectOptions: statusFilterOptions,
        accessorFn: (row: any) => {
          return t(`${row.status}`);
        },
      },
      {
        header: t("name"),
        accessorKey: "name",
        id: "name",
        required: true,
        accessorFn: (row: any) => {
          return row?.name;
        },
      },
      {
        header: t("entity_type"),
        accessorKey: "target.type",
        childEntityName: "target",
        id: "entity_type",
        accessorFn: (row: any) => {
          return row?.target?.entity_type;
        },
      },
      {
        header: t("entity_id"),
        accessorKey: "target.id",
        childEntityName: "target",
        id: "entity_id",
        accessorFn: (row: any) => {
          return row?.target?.entity_id;
        },
      },
      {
        header: t("file_name_single"),
        accessorKey: "target.kwargs.filename",
        childEntityName: "target",
        id: "filename",
        customColumnFilterId: "filename",
        customSortingKey: "filename",
        accessorFn: (row: any) => {
          return row?.target?.kwargs?.filename;
        },
      },
      {
        header: t("created_on"),
        accessorKey: "created_on",
        sortingFn: "datetime",
        filterVariant: "date",
        id: "created_on",
        Cell: ({ cell }: any) => {
          return <span>{replaceDate(cell.getValue())}</span>;
        },
      },
      {
        header: t("created_by"),
        accessorKey: "created_by",
        customColumnFilterId: "created_by_name",
        id: "created_by",
        Cell: ({ cell }: any) => {
          return <span>{cell.getValue()?.name}</span>;
        },
      },
      {
        header: t("modified_on"),
        accessorKey: "modified_on",
        id: "modified_on",
        filterVariant: "date",
        sortingFn: "datetime",
        Cell: ({ cell }: any) => {
          return <span>{replaceDate(cell.getValue())}</span>;
        },
      },
      {
        header: t("modified_by"),
        accessorKey: "modified_by",
        customColumnFilterId: "modified_by_name",
        id: "modified_by",
        Cell: ({ cell }: any) => {
          return <span>{cell.getValue()?.name}</span>;
        },
      },
    ],
    []
  );
  const triggerRefresh = () => {
    setRefreshTable((prev) => !prev);
  };
  const forceCompleteTask = async (row: any) => {
    const isConfirmed = await showConfirmationModalAsync(
      t("force_complate_task_confirm")
    );
    if (!isConfirmed) return;

    const additionalRouteParts: any = {};
    additionalRouteParts["force_complete"] = "/";
    api.genericApiRequest({
      method: "put",
      entity: "taskTracking",
      entityId: row.id,

      additionalRouteParts: additionalRouteParts,
      successHandler: () => {
        triggerRefresh();
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };
  const customRowActions = (row: any) => {
    const shouldRenderCheckedIcon = row.original?.approved_on == null;

    return (
      <>
        {shouldRenderCheckedIcon && (
          <Tooltip arrow placement="right" title={t("force_complete_task")}>
            <span>
              <GenericButton
                onClick={() => forceCompleteTask(row.original)}
                variant="icon"
                color="iconBase"
              >
                <CheckIcon />
              </GenericButton>
            </span>
          </Tooltip>
        )}
      </>
    );
  };

  return (
    <Restricted permissionRequired="frontend_view_tasktrackinglist">
      <GenericCrudTable
        columns={tasktrackingColumns}
        entity="taskTracking"
        disableAddItem
        hideEditButtonForRows
        hideDeleteButtonForRows
        customRowActionColSize={70}
        customRowActions={customRowActions}
        refreshTable={refreshTable}
        showColumnFilterbyDefault
        customColumnVisibility={{
          created_by: true,
          modified_on: true,
          modified_by: true,
        }}
      />
      {errorModal}
    </Restricted>
  );
}
